import { Boton, Pasos, Seguro } from './ui.js';

/**
 * El frame PERMISOS del Figma: la hoja "Antes de empezar".
 *
 * ── ESTA PANTALLA NO PIDE NINGÚN PERMISO, Y ES LO CORRECTO ─────────────────────
 *
 * El permiso lo pide el NAVEGADOR, con su propio diálogo, cuando `useCamara` arranca en la
 * pantalla siguiente. Esta hoja existe para que ese diálogo no caiga de sorpresa: quien
 * entiende para qué le piden la cámara dice que sí; quien ve un diálogo súbito dice que no —
 * y en iOS ese "no" es terminal (ver `camara.ts`).
 *
 * Pedir `getUserMedia` aquí solo para "calentar" el permiso encendería la cámara en una
 * pantalla que no la muestra: una luz de cámara encendida sin vídeo visible es exactamente el
 * tipo de cosa que hace que una persona cierre todo, y haría bien.
 *
 * ── LA TARJETA DE GPS ──────────────────────────────────────────────────────────
 *
 * Está en el diseño y se muestra, pero HOY no se pide geolocalización: el flujo no la usa
 * todavía y la Permissions-Policy del contenedor la tiene apagada (`geolocation=()`).
 * Cuando la verificación de ubicación exista, esto se convierte en el aviso previo de ese
 * permiso; mientras tanto, avisa de lo que el producto hará — no miente sobre lo que hace
 * hoy, porque no afirma que se pida ahora.
 *
 * ── ERRATAS DEL FIGMA, CORREGIDAS A PROPÓSITO ──────────────────────────────────
 *
 * El frame dice "Para verfificación de ubicación" y "acceder a la camara de micrófono".
 * Lo primero es un typo. Lo segundo además promete un permiso que NO pedimos nunca —el
 * micrófono está bloqueado por Permissions-Policy (`microphone=()`)— y anunciar un permiso
 * que no se pide asusta gratis. El diseño manda en lo visual; en lo que se AFIRMA, manda lo
 * que el producto hace de verdad.
 */

const TARJETAS: readonly { titulo: string; texto: string; icono: JSX.Element }[] = [
  {
    titulo: 'Cámara',
    texto: 'Para capturar tu DNI y tu rostro',
    icono: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden>
        <rect x="2" y="5" width="16" height="12" rx="3" stroke="currentColor" strokeWidth="1.5" />
        <circle cx="10" cy="11" r="3.2" stroke="currentColor" strokeWidth="1.5" />
        <path d="M7 5l1.2-2h3.6L13 5" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    titulo: 'GPS',
    texto: 'Para verificación de ubicación',
    icono: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden>
        <path
          d="M10 18s-6-5.1-6-9.5a6 6 0 1 1 12 0C16 12.9 10 18 10 18Z"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinejoin="round"
        />
        <circle cx="10" cy="8.5" r="2.2" stroke="currentColor" strokeWidth="1.5" />
      </svg>
    ),
  },
  {
    titulo: 'Privacidad',
    texto: 'Tus imágenes no se guardan en el dispositivo',
    icono: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden>
        <path
          d="M10 2 3.5 4.4v4.8c0 4.2 2.8 7.4 6.5 8.4 3.7-1 6.5-4.2 6.5-8.4V4.4L10 2Z"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinejoin="round"
        />
        <path d="m7.2 10 2 2 3.6-3.8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
];

/** Los tres sellos del pie: TLS 1.3 · ISO 30107-3 · RENIEC, cada uno con su candado. */
function Sello({ texto }: { texto: string }) {
  return (
    <span
      className="inline-flex items-center gap-1 text-[11px] font-semibold"
      style={{ color: '#12A150' }}
    >
      <svg width="9" height="11" viewBox="0 0 12 14" fill="none" aria-hidden>
        <path d="M3 6V4a3 3 0 1 1 6 0v2" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        <rect x="1.5" y="6" width="9" height="7" rx="2" fill="currentColor" />
      </svg>
      {texto}
    </span>
  );
}

export function Permisos({
  onContinuar,
  onVolver,
}: {
  onContinuar: () => void;
  onVolver?: () => void;
}) {
  return (
    <div
      className="mx-auto flex min-h-[100dvh] w-full max-w-[440px] flex-col"
      style={{
        // En el Figma la hoja flota sobre el vídeo de la cámara. Aquí todavía NO hay cámara
        // —el permiso es justo lo que esta pantalla prepara— así que el fondo es oscuro a
        // secas. Enseñar vídeo aquí exigiría el permiso antes de explicarlo: el orden
        // exactamente contrario al que esta pantalla existe para conseguir.
        background: 'linear-gradient(180deg, #101114, #1c1d24)',
        fontFamily: 'var(--tipografia, Inter, system-ui, sans-serif)',
      }}
    >
      <Pasos paso={1} onVolver={onVolver} sobreVideo />

      <div className="flex flex-1 items-end px-4 pb-4 pt-6">
        {/* La hoja blanca. */}
        <div className="w-full rounded-[28px] bg-white px-6 pb-6 pt-6" style={{ color: '#101114' }}>
          <div className="flex items-start justify-between">
            <h1 className="text-[24px] font-bold leading-tight">Antes de empezar</h1>
            <Seguro />
          </div>
          <p className="mt-1.5 text-[13.5px] leading-relaxed" style={{ color: 'rgba(16,17,20,.6)' }}>
            Necesitamos tu permiso para acceder a la cámara.
          </p>

          <div className="mt-5 flex flex-col gap-3">
            {TARJETAS.map((t) => (
              <div
                key={t.titulo}
                className="flex items-center gap-4 rounded-2xl px-4 py-3.5"
                style={{ border: '1.5px dashed rgba(51,50,242,.35)' }}
              >
                <span
                  className="grid h-11 w-11 shrink-0 place-items-center rounded-xl"
                  style={{ background: 'rgba(51,50,242,.07)', color: 'var(--acento, #3332f2)' }}
                >
                  {t.icono}
                </span>
                <div>
                  <p className="text-[14.5px] font-bold" style={{ color: 'var(--acento, #3332f2)' }}>
                    {t.titulo}
                  </p>
                  <p className="text-[13px]" style={{ color: 'rgba(16,17,20,.55)' }}>
                    {t.texto}
                  </p>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-6">
            <Boton onClick={onContinuar}>Permitir y continuar</Boton>
          </div>

          <div className="mt-4 flex items-center justify-center gap-4">
            <Sello texto="TLS 1.3" />
            <Sello texto="ISO 30107-3" />
            <Sello texto="RENIEC" />
          </div>
        </div>
      </div>
    </div>
  );
}
